import { useId, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';

import {
  formatMarginCenterCurrency,
  formatMarginCenterNumber,
} from '../utils/numberFormat';

import {
  buildRelationshipEconomicInvestigation,
} from '../utils/relationshipEconomicInvestigation';

import {
  buildRelationshipEconomicInterpretation,
} from '../utils/relationshipEconomicInterpretation';

import type {
  RelationshipAlignedBucket,
  RelationshipSeriesPoint,
} from '../utils/relationshipEconomicInvestigation';

import type {
  MarginCenterCustomerProductRecentTransaction,
  MarginCenterCustomerProductSelection,
  MarginCenterCustomerRelationshipCoverage,
  MarginCenterCustomerRelationshipPeriodMetrics,
} from '../types/marginCenter';

import type {
  MarginCenterEconomicMetricKey,
  MarginCenterEconomicReading,
  MarginCenterEconomicReadingKey,
} from '../utils/customerProductEconomicReadings';

const EXPLANATIONS: Record<MarginCenterEconomicReadingKey, string> = {
  revenue_margin:
    'Los tramos de ingresos y margen pueden evolucionar de manera distinta. El margen de cada tramo se calcula sobre ingresos con coste resuelto: no se promedian los porcentajes de las operaciones ni de los tramos.',
  price_units:
    'Se observa en qué tramos cambia el precio neto medio y en cuáles cambia la cantidad. La coincidencia temporal no mide elasticidad ni demuestra que un movimiento explique el otro.',
  cost_margin:
    'El coste unitario y el margen por tramo solo se muestran cuando son calculables. Una cobertura parcial puede limitar su comparación; no se considera coste desconocido igual a cero.',
};

function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
}

function formatInterval(point: RelationshipSeriesPoint): string {
  const start = formatDate(point.covered_start);
  const end = formatDate(point.covered_end);
  return start === end ? start : `${start} – ${end}`;
}

function formatValue(
  value: number | null,
  key: MarginCenterEconomicMetricKey,
  currencyCode: string,
  difference = false,
): string {
  if (value === null || !Number.isFinite(value)) return 'No calculable';
  const sign = difference && value > 0 ? '+' : '';
  if (key === 'margin_percentage') {
    return `${sign}${formatMarginCenterNumber(value)} ${difference ? 'p.p.' : '%'}`;
  }
  if (key === 'units') return `${sign}${formatMarginCenterNumber(value)}`;
  return `${sign}${formatMarginCenterCurrency(value, currencyCode)}`;
}

function metricValue(
  point: RelationshipSeriesPoint,
  key: MarginCenterEconomicMetricKey,
): number | null {
  const value = point[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function differenceColor(value: number | null): string {
  if (value === null || value === 0) return '#94a3b8';
  return value > 0 ? '#34d399' : '#fb7185';
}

function MetricBucketList({
  heading,
  rows,
  keyName,
  currencyCode,
}: {
  heading: string;
  rows: RelationshipAlignedBucket[];
  keyName: MarginCenterEconomicMetricKey;
  currencyCode: string;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ color: '#e2e8f0', fontWeight: 800, mb: 0.75 }}>
        {heading}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          No se observan diferencias calculables en estos tramos.
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {rows.map((row) => {
            const a = metricValue(row.current, keyName);
            const b = metricValue(row.comparison, keyName);
            const delta = a === null || b === null ? null : a - b;
            return (
              <Box
                key={row.index}
                sx={{
                  p: 1,
                  border: '1px solid rgba(148,163,184,0.13)',
                  borderRadius: 1.5,
                  bgcolor: 'rgba(15,23,42,0.50)',
                }}
              >
                <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
                  <Typography variant="caption" sx={{ color: '#e2e8f0', fontWeight: 750 }}>
                    {formatInterval(row.current)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: differenceColor(delta), fontWeight: 850, fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(delta, keyName, currencyCode, true)}
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: '#94a3b8', lineHeight: 1.45 }}>
                  Actual {formatValue(metricValue(row.current, keyName), keyName, currencyCode)} ·
                  {' '}Referencia {formatValue(metricValue(row.comparison, keyName), keyName, currencyCode)}
                  {' '}({formatInterval(row.comparison)})
                </Typography>
                {(row.current.is_partial_bucket || row.comparison.is_partial_bucket) && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.2, color: '#fbbf24' }}>
                    Incluye un tramo parcial.
                  </Typography>
                )}
                {keyName === 'average_unit_cost' || keyName === 'margin_percentage' ? (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.15, color: '#64748b' }}>
                    Cobertura de coste por filas: actual {formatMarginCenterNumber(row.current.cost_coverage_pct)} % ·
                    {' '}referencia {formatMarginCenterNumber(row.comparison.cost_coverage_pct)} %.
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function ActivityInsight({
  current,
  comparison,
  reading,
}: {
  current: MarginCenterCustomerRelationshipPeriodMetrics;
  comparison: MarginCenterCustomerRelationshipPeriodMetrics | null;
  reading: MarginCenterEconomicReading;
}) {
  const average = (units: number, count: number) =>
    count > 0 ? formatMarginCenterNumber(units / count) : 'No calculable';
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2" sx={{ color: '#e2e8f0', fontWeight: 800, mb: 0.7 }}>
        Actividad que acompaña el movimiento
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.8 }}>
        {[
          { label: 'Operaciones', now: formatMarginCenterNumber(current.transaction_count, 0), before: comparison ? formatMarginCenterNumber(comparison.transaction_count, 0) : '—' },
          { label: 'Días con compra', now: formatMarginCenterNumber(current.active_days, 0), before: comparison ? formatMarginCenterNumber(comparison.active_days, 0) : '—' },
          { label: 'Unidades por operación · media', now: average(current.units, current.transaction_count), before: comparison ? average(comparison.units, comparison.transaction_count) : '—' },
        ].map((item) => (
          <Box key={item.label} sx={{ p: 1, borderRadius: 1.5, bgcolor: 'rgba(15,23,42,0.46)', border: '1px solid rgba(148,163,184,0.12)' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 750 }}>{item.label}</Typography>
            <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 850, mt: 0.3 }}>{item.now}</Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Referencia: {item.before}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.6 }}>
        La media por operación se deriva de unidades y recuento contractual. No describe la compra habitual ni determina la causa del cambio en {reading.title.toLowerCase()}.
      </Typography>
    </Box>
  );
}

export default function CustomerRelationshipEconomicReadingDetail({
  reading,
  current,
  comparison,
  selection,
  coverage,
  series,
  recentTransactions,
  currencyCode,
  onClose,
}: {
  reading: MarginCenterEconomicReading;
  current: MarginCenterCustomerRelationshipPeriodMetrics;
  comparison: MarginCenterCustomerRelationshipPeriodMetrics | null;
  selection: MarginCenterCustomerProductSelection;
  coverage: MarginCenterCustomerRelationshipCoverage | null;
  series: RelationshipSeriesPoint[];
  recentTransactions: MarginCenterCustomerProductRecentTransaction[];
  currencyCode: string;
  onClose: () => void;
}) {
  const insight = useMemo(
    () => buildRelationshipEconomicInvestigation(series, reading),
    [series, reading],
  );
  const availableInPeriod = recentTransactions.filter((row) =>
    (!selection.start_date || row.transaction_date.slice(0, 10) >= selection.start_date)
    && (!selection.end_date || row.transaction_date.slice(0, 10) <= selection.end_date)
  );
  const highlightedRows = [...insight.primaryLeaders, ...insight.secondaryLeaders];
  const availableInHighlightedTramos = availableInPeriod.filter((row) =>
    highlightedRows.some((bucket) =>
      row.transaction_date.slice(0, 10) >= bucket.current.covered_start
      && row.transaction_date.slice(0, 10) <= bucket.current.covered_end
    )
  );
  const recentInPeriod = (
    availableInHighlightedTramos.length > 0
      ? availableInHighlightedTramos
      : availableInPeriod
  ).slice(0, 4);
  const showCostCoverage = reading.key !== 'price_units' && coverage !== null;
  const { directions } = insight;
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const evidenceId = useId();
  const interpretation = useMemo(
    () => buildRelationshipEconomicInterpretation({ reading, insight, current, comparison, coverage }),
    [reading, insight, current, comparison, coverage],
  );

  return (
    <Paper
      elevation={0}
      role="region"
      aria-label={`Investigación: ${reading.title}`}
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        border: '1px solid rgba(56,189,248,0.30)',
        background: 'linear-gradient(180deg, rgba(14,32,49,0.98), rgba(15,23,42,0.97))',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 800, letterSpacing: 0.7 }}>
            Investigación del comportamiento
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 850 }}>
            {reading.title}: ¿dónde se produce el movimiento?
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.4, color: '#94a3b8', lineHeight: 1.5 }}>
            Primero presentamos la lectura observacional; después puedes examinar los tramos y las operaciones que la sustentan.
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onClose} startIcon={<CloseRoundedIcon fontSize="small" />}
          sx={{ flexShrink: 0, color: '#94a3b8', borderColor: 'rgba(148,163,184,0.30)', textTransform: 'none' }}>
          Cerrar
        </Button>
      </Stack>

      <Box sx={{ mt: 1.3, p: { xs: 1.3, md: 1.6 }, borderRadius: 1.6, bgcolor: 'rgba(56,189,248,0.075)', border: '1px solid rgba(56,189,248,0.24)' }}>
        <Typography variant="overline" sx={{ color: '#7dd3fc', fontWeight: 850 }}>
          Lectura comercial · hechos observados
        </Typography>
        <Typography variant="subtitle1" sx={{ mt: 0.35, color: '#f8fafc', fontWeight: 850, lineHeight: 1.45 }}>
          {interpretation.headline}
        </Typography>

        <Typography variant="caption" sx={{ color: '#93c5fd', display: 'block', mt: 1.2, fontWeight: 800 }}>
          ¿Dónde se aprecia el cambio?
        </Typography>
        <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.35, lineHeight: 1.55 }}>
          {interpretation.observation}
        </Typography>

        <Typography variant="caption" sx={{ color: '#93c5fd', display: 'block', mt: 1.15, fontWeight: 800 }}>
          ¿Cómo está comprando este cliente?
        </Typography>
        <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.35, lineHeight: 1.55 }}>
          {interpretation.activity}
        </Typography>

        <Typography variant="caption" sx={{ color: '#93c5fd', display: 'block', mt: 1.15, fontWeight: 800 }}>
          {interpretation.secondaryTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.35, lineHeight: 1.55 }}>
          {interpretation.secondary}
        </Typography>

        <Box sx={{ mt: 1.3, px: 1.1, py: 1, borderRadius: 1.2, bgcolor: 'rgba(15,23,42,0.62)', border: '1px solid rgba(148,163,184,0.13)' }}>
          <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', fontWeight: 800 }}>
            Qué conviene tener en cuenta
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.35, lineHeight: 1.5 }}>
            {interpretation.caution}
          </Typography>
        </Box>
      </Box>

      <Button
        size="small"
        variant="outlined"
        onClick={() => setEvidenceOpen((open) => !open)}
        aria-expanded={evidenceOpen}
        aria-controls={evidenceId}
        sx={{ mt: 1.25, textTransform: 'none', borderColor: 'rgba(148,163,184,0.28)', color: '#7dd3fc' }}
      >
        {evidenceOpen ? 'Ocultar desglose y evidencia' : 'Ver desglose y evidencia'}
      </Button>
      <Collapse in={evidenceOpen} unmountOnExit>
        <Box id={evidenceId} sx={{ mt: 1.25 }}>
          <Box sx={{ p: 1.15, borderRadius: 1.5, bgcolor: 'rgba(56,189,248,0.055)', border: '1px solid rgba(56,189,248,0.14)' }}>
        <Typography variant="caption" sx={{ color: '#7dd3fc', display: 'block', fontWeight: 800 }}>
          Patrón observado por tramos
        </Typography>
        {directions.evaluable > 0 ? (
          <Typography variant="body2" sx={{ color: '#e2e8f0', mt: 0.4, lineHeight: 1.6 }}>
            De {insight.aligned.length} tramos alineados, {directions.evaluable} permiten comparar ambas métricas:
            {' '}{directions.bothRise} con ambas al alza, {directions.bothFall} con ambas a la baja,
            {' '}{directions.opposite} con direcciones opuestas y {directions.unchangedInAtLeastOne} con al menos una sin variación.
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.4 }}>
            No existen tramos alineados con las dos métricas calculables. La variación agregada no autoriza a inventar datos de detalle.
          </Typography>
        )}
        {insight.partialOrUnequalBuckets > 0 && (
          <Typography variant="caption" sx={{ color: '#fbbf24', display: 'block', mt: 0.5 }}>
            {insight.partialOrUnequalBuckets} tramos tienen cobertura temporal parcial o duración distinta. Se muestran sus magnitudes observadas, sin extrapolar ni normalizar.
          </Typography>
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1.6, mb: 0.35, color: '#f8fafc', fontWeight: 850 }}>
        Tramos con mayores diferencias observadas
      </Typography>
      <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 1, lineHeight: 1.5 }}>
        Cada bloque ordena independientemente por diferencia absoluta de su métrica, con hasta tres tramos. Se comparan por posición temporal equivalente, no por la misma fecha del calendario. Las diferencias de porcentajes se expresan en puntos porcentuales y no se agregan entre tramos.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
        <MetricBucketList heading={reading.primary.label} rows={insight.primaryLeaders} keyName={reading.primary.key} currencyCode={currencyCode} />
        <MetricBucketList heading={reading.secondary.label} rows={insight.secondaryLeaders} keyName={reading.secondary.key} currencyCode={currencyCode} />
      </Box>

      <ActivityInsight current={current} comparison={comparison} reading={reading} />

      {showCostCoverage && coverage && (
        <Box sx={{ mt: 1.3, p: 1, borderRadius: 1.5, border: '1px solid rgba(148,163,184,0.16)' }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#e2e8f0', fontWeight: 800 }}>
            Cobertura financiera del margen / coste
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', mt: 0.3 }}>
            Filas con coste resuelto: actual {formatMarginCenterNumber(coverage.current.cost_coverage_pct)} % · comparación {coverage.comparison ? `${formatMarginCenterNumber(coverage.comparison.cost_coverage_pct)} %` : 'No disponible'}.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8' }}>
            Cobertura por ingresos: actual {formatMarginCenterNumber(coverage.current.resolved_revenue_coverage_pct)} % · comparación {coverage.comparison ? `${formatMarginCenterNumber(coverage.comparison.resolved_revenue_coverage_pct)} %` : 'No disponible'}.
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle2" sx={{ mt: 1.45, mb: 0.55, color: '#f8fafc', fontWeight: 850 }}>
        {availableInHighlightedTramos.length > 0
          ? 'Operaciones disponibles en los tramos destacados'
          : 'Operaciones recientes disponibles en el periodo'}
      </Typography>
      {recentInPeriod.length > 0 ? (
        <Stack spacing={0.55}>
          {recentInPeriod.map((row) => (
            <Stack key={row.transaction_id} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={0.5}
              sx={{ p: 0.9, borderRadius: 1.2, bgcolor: 'rgba(15,23,42,0.45)', border: '1px solid rgba(148,163,184,0.10)' }}>
              <Typography variant="caption" sx={{ color: '#cbd5e1', fontWeight: 750 }}>
                {formatDate(row.transaction_date)} · {formatMarginCenterNumber(row.quantity)} unidades
              </Typography>
              <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                Ingresos {formatMarginCenterCurrency(row.revenue, currencyCode)} · precio {formatMarginCenterCurrency(row.net_unit_price, currencyCode)} / unidad
                {' '}· {row.cost_resolved && row.margin_percentage !== null
                  ? `margen de la operación ${formatMarginCenterNumber(row.margin_percentage)} %`
                  : 'margen de la operación no calculable'}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          La muestra de últimas operaciones devuelta por el contrato no contiene registros de este periodo.
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.55, color: '#64748b', lineHeight: 1.5 }}>
        Se utilizan únicamente las últimas operaciones devueltas por el contexto (máximo 10 hasta el fin del periodo, incluso anteriores a su inicio), priorizando las disponibles en tramos destacados. Esta selección no es exhaustiva ni explica por sí sola la variación agregada. El contrato de esta muestra no incluye coste unitario por operación.
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.2, color: '#64748b', lineHeight: 1.5 }}>
        {EXPLANATIONS[reading.key]} La evolución conjunta solo describe hechos simultáneos; no atribuye causas ni recomienda un cambio de precio.
      </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
}
